// Path: C:\gestion-copro\src\types\crm.types.ts
// Session 187 : Module CRM — Tunnel de vente, prospects, commissions
// Session 189 : Enrichissement prospect pour SENTINELLE moteur de prospection
// Session 189 : Responsables dynamiques — plus de hardcoding Dominique/Alain
// Session 193 : Remplacement TierProspect (1|2|3) → SegmentProspect (labels métier)
// Session 194 : Ajout contenuHtml dans HistoriqueMailProspect — brouillon ouvrable depuis Messagerie
// Session 199 : Ajout DocumentProspect (PDFs déposés) + MessageConversationProspect (chat SENTINELLE persisté)
// Session 206 : Ajout TypeMail ('recu') + analyseReponse dans HistoriqueMailProspect — réponses entrantes

import { Timestamp } from 'firebase/firestore';

// ============================================
// SEGMENT PROSPECT (ex-Tier)
// ============================================

/**
 * Segment stratégique du prospect — décrit la nature de la relation commerciale.
 * ambassadeur   = associations/fédérations nationales + syndics partenaires (effet de levier massif)
 * client_direct = copropriétaire ou CS contacté directement — souscrit lui-même
 * recommandeur  = notaire, avocat, syndic bénévole, gestionnaire — oriente sans acheter
 *
 * Ordre de priorité : ambassadeur → client_direct → recommandeur
 * Session 198 : renommage depuis multiplicateur/prescripteur/direct — décision Dominique + Alain
 */
export type SegmentProspect = 'ambassadeur' | 'client_direct' | 'recommandeur';

export const LABELS_SEGMENT_PROSPECT: Record<SegmentProspect, string> = {
  ambassadeur: 'Ambassadeur',
  client_direct: 'Client direct',
  recommandeur: 'Recommandeur',
};

export const DESCRIPTIONS_SEGMENT_PROSPECT: Record<SegmentProspect, string> = {
  ambassadeur:
    'Un accord unique, un impact massif. Recommande Copro Protect à toute sa communauté — adhérents, membres, copropriétés clientes. Exemples : ARC, UNARC, UNPI, ANCC, CLCV, AFCopro. Certains syndics modernes entrent aussi dans cette catégorie s\'ils intègrent Copro Protect dans leur offre.',
  client_direct:
    'Il est à la fois prospect et futur utilisateur — il souscrit pour lui-même. Exemples : président de CS d\'une résidence de 30 lots, copropriétaire bailleur isolé, syndic bénévole cherchant à outiller son CS.',
  recommandeur:
    'Il conseille, ses clients achètent. Oriente ponctuellement vers Copro Protect sans l\'utiliser lui-même. Exemples : avocat spécialisé copropriété, notaire, expert-comptable de syndic, journaliste immobilier, syndic professionnel partiellement convaincu.',
};

export const COULEURS_SEGMENT_PROSPECT: Record<SegmentProspect, string> = {
  ambassadeur:   'bg-emerald-100 text-emerald-800 border-emerald-300',
  client_direct: 'bg-purple-100 text-purple-800 border-purple-300',
  recommandeur:  'bg-blue-100 text-blue-800 border-blue-300',
};

/** Couleur de la bordure gauche des cartes Kanban */
export const BORDURE_SEGMENT_PROSPECT: Record<SegmentProspect, string> = {
  ambassadeur:   'border-l-emerald-500',
  client_direct: 'border-l-purple-500',
  recommandeur:  'border-l-blue-500',
};

// ============================================
// STATUTS DU PIPELINE
// ============================================

/** Étapes du tunnel de prospection */
export type StatutProspect =
  | 'identifie'     // Prospect repéré, pas encore contacté
  | 'contacte'      // Premier contact effectué
  | 'interesse'     // A montré de l'intérêt, échange en cours
  | 'demo'          // Démo ou essai en cours
  | 'converti'      // A souscrit un abonnement Copro Protect
  | 'perdu';        // Prospect abandonné ou refusé

/** Libellés affichés pour chaque statut */
export const LABELS_STATUT_PROSPECT: Record<StatutProspect, string> = {
  identifie: 'Identifié',
  contacte: 'Contacté',
  interesse: 'Intéressé',
  demo: 'Démo',
  converti: 'Converti',
  perdu: 'Perdu',
};

/** Couleurs Tailwind pour chaque colonne Kanban */
export const COULEURS_STATUT_PROSPECT: Record<StatutProspect, string> = {
  identifie: 'bg-gray-100 border-gray-300',
  contacte: 'bg-blue-50 border-blue-300',
  interesse: 'bg-amber-50 border-amber-300',
  demo: 'bg-purple-50 border-purple-300',
  converti: 'bg-green-50 border-green-300',
  perdu: 'bg-red-50 border-red-300',
};

/** Colonnes visibles dans le Kanban (perdu = masqué par défaut, accessible par filtre) */
export const COLONNES_KANBAN: StatutProspect[] = [
  'identifie',
  'contacte',
  'interesse',
  'demo',
  'converti',
];

// ============================================
// TYPE DE CIBLE
// ============================================

/** Quel produit Copro Protect vise-t-on pour ce prospect ? */
export type TypeCibleProspect =
  | 'vigie'            // Copropriétaire individuel
  | 'cs_sentinelle'    // CS ≤ 15 lots — 239 €/an
  | 'cs_bouclier'      // CS 16-40 lots — 419 €/an
  | 'cs_forteresse';   // CS > 40 lots — 599 €/an

export const LABELS_TYPE_CIBLE: Record<TypeCibleProspect, string> = {
  vigie: 'VIGIE (copropriétaire solo)',
  cs_sentinelle: 'CS SENTINELLE (≤ 15 lots)',
  cs_bouclier: 'CS BOUCLIER (16-40 lots)',
  cs_forteresse: 'CS FORTERESSE (> 40 lots)',
};

// ============================================
// TYPE DE PROSPECT
// ============================================

/** Nature du prospect — association, copropriété directe, ou individu */
export type TypeProspect =
  | 'association'      // Association de copropriétaires (ARC, UNPI, ANCC...)
  | 'copropriete'      // Copropriété identifiée (CS ou syndic bénévole)
  | 'individu'         // Copropriétaire individuel
  | 'prescripteur';    // Quelqu'un qui peut recommander (avocat, notaire, courtier...)

export const LABELS_TYPE_PROSPECT: Record<TypeProspect, string> = {
  association: 'Association',
  copropriete: 'Copropriété',
  individu: 'Individu',
  prescripteur: 'Prescripteur',
};

// ============================================
// RESPONSABLE (DYNAMIQUE)
// ============================================

/**
 * Responsable d'un prospect = identifiant string.
 * Chargé dynamiquement depuis Firestore (admin + apporteurs).
 * Plus aucun hardcoding.
 */
export type ResponsableProspect = string;

/** Structure d'un responsable chargé dynamiquement */
export interface InfoResponsable {
  identifiant: string;         // Ex: 'dominique', 'alain', 'marie'...
  nomComplet: string;          // Ex: 'Dominique SERGENT', 'Alain LAZARSKI'
  couleurClasse: string;       // Classe Tailwind pour le badge (ex: 'bg-emerald-100 text-emerald-700')
}

// ============================================
// NOTE DE SUIVI
// ============================================

export interface NoteSuivi {
  id: string;
  date: Timestamp;
  texte: string;
  auteur: ResponsableProspect;
}

// ============================================
// HISTORIQUE MAILS SENTINELLE
// ============================================

/** Type de template utilisé pour le mail */
export type TypeTemplateMail =
  | 'premier_contact'       // Après un premier appel téléphonique
  | 'relance_demo'          // Relance après une démo ou un échange
  | 'proposition_partenariat' // Proposition de partenariat (associations)
  | 'libre';                // Rédaction libre SENTINELLE

export const LABELS_TYPE_TEMPLATE: Record<TypeTemplateMail, string> = {
  premier_contact: 'Premier contact',
  relance_demo: 'Relance après démo',
  proposition_partenariat: 'Proposition partenariat',
  libre: 'Rédaction libre',
};

/**
 * Type du mail dans la timeline.
 * brouillon = généré par SENTINELLE, pas encore envoyé
 * envoye    = envoyé via Gmail CRM
 * recu      = réponse du prospect importée manuellement en PDF (Session 206)
 */
export type TypeMail = 'brouillon' | 'envoye' | 'recu';

/**
 * Analyse SENTINELLE d'une réponse importée.
 * Produite lors de l'import PDF d'une réponse prospect.
 * Stockée dans le champ analyseReponse de HistoriqueMailProspect (type='recu').
 */
export interface AnalyseReponseProspect {
  resume: string;                // Résumé de la réponse en 2-3 lignes
  pointsTraites: string[];       // Points du mail envoyé effectivement traités dans la réponse
  pointsIgnores: string[];       // Points du mail envoyé ignorés ou éludés dans la réponse
  qualification: 'favorable' | 'neutre' | 'evasif' | 'partiel' | 'refus';
  recommandation: string;        // Action recommandée : relancer / proposer démo / clore
  brouillonRelance?: string;     // Brouillon de relance SCUD (présent si points ignorés)
}

export const LABELS_QUALIFICATION_REPONSE: Record<AnalyseReponseProspect['qualification'], string> = {
  favorable: 'Favorable',
  neutre: 'Neutre',
  evasif: 'Évasif',
  partiel: 'Partiel',
  refus: 'Refus',
};

export const COULEURS_QUALIFICATION_REPONSE: Record<AnalyseReponseProspect['qualification'], string> = {
  favorable: 'bg-emerald-100 text-emerald-800',
  neutre:    'bg-gray-100 text-gray-700',
  evasif:    'bg-amber-100 text-amber-800',
  partiel:   'bg-blue-100 text-blue-800',
  refus:     'bg-red-100 text-red-800',
};

/** Trace d'un mail dans la timeline — envoyé, brouillon, ou réponse reçue */
export interface HistoriqueMailProspect {
  id: string;
  date: Timestamp;
  objet: string;
  template: TypeTemplateMail;
  destinataire: string;
  auteur: ResponsableProspect;
  envoye: boolean;
  apercu?: string;
  contenuHtml?: string;          // HTML complet — brouillon ouvrable depuis Messagerie
  // Session 206 — champs réponses entrantes
  type?: TypeMail;               // 'brouillon' | 'envoye' | 'recu' — optionnel pour rétrocompatibilité
  fichierPdfUrl?: string;        // URL Storage du PDF importé (type='recu' uniquement)
  analyseReponse?: AnalyseReponseProspect; // Analyse SENTINELLE (type='recu' uniquement)
}

// ============================================
// DOCUMENT PROSPECT — SESSION 199
// ============================================

/**
 * Type de document déposé dans la fiche prospect.
 * reponse_recue  = mail reçu du prospect, imprimé en PDF et déposé
 * proposition    = proposition commerciale envoyée
 * compte_rendu   = compte-rendu d'appel ou de réunion
 * autre          = tout autre document lié au prospect
 */
export type TypeDocumentProspect =
  | 'reponse_recue'
  | 'proposition'
  | 'compte_rendu'
  | 'autre';

export const LABELS_TYPE_DOCUMENT_PROSPECT: Record<TypeDocumentProspect, string> = {
  reponse_recue: 'Réponse reçue',
  proposition: 'Proposition',
  compte_rendu: 'Compte-rendu',
  autre: 'Document',
};

export const COULEURS_TYPE_DOCUMENT_PROSPECT: Record<TypeDocumentProspect, string> = {
  reponse_recue: 'bg-green-100 text-green-800',
  proposition:   'bg-blue-100 text-blue-800',
  compte_rendu:  'bg-amber-100 text-amber-800',
  autre:         'bg-gray-100 text-gray-700',
};

/**
 * Métadonnées d'un document déposé dans la fiche prospect.
 * Le fichier est stocké dans Firebase Storage : crm/prospects/{prospectId}/{id}.pdf
 * Les métadonnées sont dans la sous-collection : crm_prospects/{prospectId}/documents/{id}
 */
export interface DocumentProspect {
  id: string;
  type: TypeDocumentProspect;
  nom: string;           // Nom affiché (ex: "Réponse ARC 07/03/2026")
  fichierNom: string;    // Nom du fichier en Storage
  storageUrl: string;    // URL de téléchargement Firebase Storage
  taille?: number;       // Taille en octets
  auteur: ResponsableProspect;
  analyseId?: string;    // ID du message SENTINELLE qui a analysé ce document
  createdAt: Timestamp;
}

// ============================================
// CONVERSATION SENTINELLE CRM — SESSION 199
// ============================================

/**
 * Message persisté dans la conversation SENTINELLE d'un prospect.
 * Stocké dans : crm_prospects/{prospectId}/conversation/{messageId}
 * Permet de retrouver le contexte à chaque réouverture de la fiche.
 */
export interface MessageConversationProspect {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  documentId?: string;   // Si ce message est lié à un document déposé
  createdAt: Timestamp;
}

// ============================================
// PROSPECT — DOCUMENT PRINCIPAL
// ============================================

export interface Prospect {
  id: string;

  // Identification
  nom: string;
  typeProspect: TypeProspect;
  typeCible: TypeCibleProspect;
  segment: SegmentProspect;     // Segment stratégique (ambassadeur / client_direct / recommandeur)

  // Contact
  contactNom?: string;
  contactEmail?: string;
  contactTelephone?: string;
  adresse?: string;
  siteWeb?: string;

  // Pipeline
  statut: StatutProspect;
  responsable: ResponsableProspect;
  source?: string;

  // Session 189 : Enrichissement prospection SENTINELLE
  problematiques: string[];
  objectifContact: string;
  tailleStructure: string;
  prochaineAction: string;
  dateProchaineAction?: Timestamp;
  historiqueMails: HistoriqueMailProspect[];

  // Suivi
  notes: NoteSuivi[];
  datePremierContact?: Timestamp;
  dateConversion?: Timestamp;

  // Lien avec Copro Protect (rempli automatiquement à la conversion)
  clientUserId?: string;
  clientEmail?: string;
  formuleClient?: string;

  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// COMMISSION — SUIVI DES VERSEMENTS
// ============================================

export type StatutCommission = 'du' | 'verse' | 'partiel';

export const LABELS_STATUT_COMMISSION: Record<StatutCommission, string> = {
  du: 'Dû',
  verse: 'Versé',
  partiel: 'Partiellement versé',
};

export interface DetailCommissionClient {
  userId: string;
  nomClient: string;
  formule: string;
  caHTMensuel: number;
  commissionMensuelle: number;
  statutAbonnement: string;
}

export interface Commission {
  id: string;
  apporteurId: string;
  apporteurNom: string;
  trimestre: string;
  montantDu: number;
  montantVerse: number;
  resteAVerser: number;
  details: DetailCommissionClient[];
  statut: StatutCommission;
  dateVersement?: Timestamp;
  referenceVersement?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ============================================
// CONSTANTES BUSINESS
// ============================================

export const TAUX_COMMISSION = 0.25;
export const TAUX_TVA = 0.20;

export const PRIX_TTC_MENSUEL: Record<string, number> = {
  'fondateur_mensuel': 5.90,
  'fondateur_annuel': 59 / 12,
  'normal_mensuel': 14.90,
  'normal_annuel': 149 / 12,
  'cs_sentinelle_mensuel': 23.90,
  'cs_sentinelle_annuel': 239 / 12,
  'cs_bouclier_mensuel': 41.90,
  'cs_bouclier_annuel': 419 / 12,
  'cs_forteresse_mensuel': 59.90,
  'cs_forteresse_annuel': 599 / 12,
};

export const calculerCAHTMensuel = (formule: string): number => {
  const prixTTC = PRIX_TTC_MENSUEL[formule] || 0;
  return prixTTC / (1 + TAUX_TVA);
};

export const calculerCommissionMensuelle = (formule: string): number => {
  return calculerCAHTMensuel(formule) * TAUX_COMMISSION;
};
